import * as moment from 'moment';

/** ICEYE tasking constraints (business rules). */
export const ICEYE_MIN_CAPTURE_INTERVAL_HOURS = 6;
export const ICEYE_MAX_CAPTURES_PER_DAY = 4;

export interface IceyeSubdailyCapture {
  start: string;
  end: string;
}

export interface IceyeSubdailyDay {
  day: number;
  date: string;
  captures: IceyeSubdailyCapture[];
}

/**
 * Splits an ICEYE sub-daily TOI (openWindow -> closeWindow) into per-day capture windows.
 *
 * ICEYE rules:
 * - Max 4 captures per 24h day.
 * - Minimum 6h between the start of consecutive captures.
 * - `subDailyCount` is the requested captures/day; the effective count for any given day is
 *   capped by 4 AND by how many 6h-spaced captures actually fit that day's available window
 *   (partial days with < 6h available produce zero captures for that day).
 *
 * This function assumes the existing "TOI must be >= 24 hours" validation has already run
 * elsewhere (leaflet-task.component.ts `getHoursDifference()`); it is not duplicated here.
 *
 * Formatting matches the rest of the codebase: `moment(x).utcOffset(0, true).format()`.
 */
export function generateIceyeSubdailyRecurrence(
  openWindow: string | Date | moment.Moment | null | undefined,
  closeWindow: string | Date | moment.Moment | null | undefined,
  subDailyCount: number | string | null | undefined
): IceyeSubdailyDay[] {
  const toiStart = moment(openWindow).utcOffset(0, true);
  const toiEnd = moment(closeWindow).utcOffset(0, true);

  const requestedCount:any = subDailyCount;

  if ( !toiStart.isValid() || !toiEnd.isValid() || !toiStart.isBefore(toiEnd) ) {
    return [];
  }

  const days: IceyeSubdailyDay[] = [];

  let dayNumber = 1;
  let cursor = toiStart.clone();

  while (cursor.isBefore(toiEnd)) {
    const nextMidnight = cursor
      .clone()
      .add(1, 'day')
      .startOf('day');

    const isLastDay = !nextMidnight.isBefore(toiEnd);

    const windowStart = cursor.clone();

    // This is the actual calculation boundary.
    // For a non-last day it is the next midnight.
    // For the last day it is the user's selected End Time.
    const windowEnd = isLastDay ? toiEnd.clone() : nextMidnight.clone();

    const availableHours = windowEnd.diff( windowStart, 'hours', true );

    // ICEYE requires a minimum 6-hour interval.
    const maxPossibleCaptures = Math.floor( availableHours / ICEYE_MIN_CAPTURE_INTERVAL_HOURS );

    const capturesForDay = Math.min( requestedCount, ICEYE_MAX_CAPTURES_PER_DAY, maxPossibleCaptures );

    const captures: IceyeSubdailyCapture[] = [];

    if (capturesForDay > 0) {
        const intervalHours =
            availableHours / capturesForDay;

        let captureStart = windowStart.clone();

        for (let i = 0; i < capturesForDay; i++) {
            // Actual boundary used for calculation.
            const actualCaptureEnd = i === capturesForDay - 1 ? windowEnd.clone() : windowStart.clone().add( intervalHours * (i + 1), 'hours' );

            // For a non-last calendar day, show 23:59:59
            // instead of the next day's 00:00:00.
            const captureEnd = i === capturesForDay - 1 && !isLastDay ? actualCaptureEnd.clone().subtract(1, 'second') : actualCaptureEnd.clone();

            captures.push({
                start: captureStart.clone().format(),
                end: captureEnd.format(),
            });

            // IMPORTANT:
            // Continue calculation from the actual boundary,
            // not the displayed 23:59:59 value.
            captureStart = actualCaptureEnd.clone();
        }
    }

    // Only add the day when at least one capture is available.
    if (captures.length > 0) {
      days.push({
        day: dayNumber,
        date: windowStart.format(),
        captures,
      });
    }

    dayNumber++;
    cursor = nextMidnight.clone();
  }

  return days;
}
